/** Pure layered pricing evaluate: materials → COGS → Wholesale → MSRP. */

export type PricingLayerBand = "cogs" | "wholesale" | "msrp";

export type PricingLayerOp = "add_usd" | "multiply" | "margin_denom";

export type PricingLayerSourceKind =
  | "none"
  | "materials"
  | "column"
  | "running_total"
  | "band_total";

export type PricingLayerColumnDef = {
  id: string;
  band: PricingLayerBand;
  key: string;
  label: string;
  op: PricingLayerOp;
  source_kind: PricingLayerSourceKind;
  source_column_id: string | null;
  source_band: PricingLayerBand | null;
  sort_order: number;
  is_active: boolean;
  /** Markup reads the cell as a fraction added on top. Stored as multiply plus this flag. */
  rate_kind?: "markup";
};

export type PricingLayerColumnResult = {
  column_id: string;
  key: string;
  band: PricingLayerBand;
  op: PricingLayerOp;
  input_value: number | null;
  source_amount: number | null;
  result_amount: number | null;
  running_after: number;
};

export const BAND_ROUND_UP_INCREMENTS = [1, 5, 10, 25, 50, 100] as const;
export type BandRoundUpIncrement = (typeof BAND_ROUND_UP_INCREMENTS)[number];

export type BandRoundUp = {
  wholesale: BandRoundUpIncrement | null;
  msrp: BandRoundUpIncrement | null;
};

export const NO_BAND_ROUND_UP: BandRoundUp = { wholesale: null, msrp: null };

export function parseBandRoundUpIncrement(raw: unknown): BandRoundUpIncrement | null {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  return (BAND_ROUND_UP_INCREMENTS as readonly number[]).includes(n) ? (n as BandRoundUpIncrement) : null;
}

export function parseBandRoundUp(raw: unknown): BandRoundUp {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return NO_BAND_ROUND_UP;
  const rec = raw as Record<string, unknown>;
  return {
    wholesale: parseBandRoundUpIncrement(rec.wholesale),
    msrp: parseBandRoundUpIncrement(rec.msrp),
  };
}

/** Read the org round-up saved on any pricing column. */
export function bandRoundUpFromColumns(columns: readonly { metadata?: unknown }[]): BandRoundUp {
  for (const column of columns) {
    const metadata = column.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) continue;
    const raw = (metadata as Record<string, unknown>).band_round_up;
    if (raw && typeof raw === "object") return parseBandRoundUp(raw);
  }
  return NO_BAND_ROUND_UP;
}

/** Ceiling to a dollar step. An amount already on the step stays put. */
export function roundUpUsd(amount: number, increment: BandRoundUpIncrement | null | undefined): number {
  if (!increment || !Number.isFinite(amount)) return amount;
  const cents = Math.round(amount * 100);
  const step = Math.round(increment * 100);
  return (Math.ceil(cents / step) * step) / 100;
}

function roundedBandTotal(band: PricingLayerBand, amount: number, roundUp: BandRoundUp | null | undefined): number {
  if (band === "wholesale") return roundUpUsd(amount, roundUp?.wholesale);
  if (band === "msrp") return roundUpUsd(amount, roundUp?.msrp);
  return round4(amount);
}

export type PricingLayerEvaluateInput = {
  materials_cost_usd: number;
  columns: PricingLayerColumnDef[];
  /** column_id → numeric input (add $, multiply factor, or margin fraction). */
  values_by_column_id: Record<string, number | null | undefined>;
  /** Optional ceiling on the wholesale and MSRP sums. MSRP starts from the rounded wholesale. */
  round_up?: BandRoundUp | null;
};

export type PricingLayerEvaluateResult = {
  materials_cost_usd: number;
  column_results: PricingLayerColumnResult[];
  cogs_total_usd: number;
  wholesale_total_usd: number;
  msrp_total_usd: number;
  warnings: string[];
};

const BAND_ORDER: PricingLayerBand[] = ["cogs", "wholesale", "msrp"];

function bandRank(band: PricingLayerBand): number {
  return BAND_ORDER.indexOf(band);
}

export function sortPricingLayerColumns(columns: PricingLayerColumnDef[]): PricingLayerColumnDef[] {
  return [...columns]
    .filter((c) => c.is_active)
    .sort((a, b) => {
      const br = bandRank(a.band) - bandRank(b.band);
      if (br !== 0) return br;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.key.localeCompare(b.key);
    });
}

function round4(n: number): number {
  return Number(n.toFixed(4));
}

function resolveSourceAmount(opts: {
  col: PricingLayerColumnDef;
  materials: number;
  running: number;
  bandTotals: Record<PricingLayerBand, number>;
  columnResults: Record<string, number>;
  warnings: string[];
}): number | null {
  const { col, materials, running, bandTotals, columnResults, warnings } = opts;
  switch (col.source_kind) {
    case "none":
      return null;
    case "materials":
      return materials;
    case "running_total":
      return running;
    case "band_total": {
      const band = col.source_band;
      if (!band) {
        warnings.push(`Column ${col.key}: band_total missing source_band.`);
        return null;
      }
      return bandTotals[band];
    }
    case "column": {
      const id = col.source_column_id;
      if (!id) {
        warnings.push(`Column ${col.key}: column source missing source_column_id.`);
        return null;
      }
      if (!(id in columnResults)) {
        warnings.push(`Column ${col.key}: source column not yet evaluated.`);
        return null;
      }
      return columnResults[id];
    }
    default:
      warnings.push(`Column ${col.key}: unknown source_kind.`);
      return null;
  }
}

/**
 * Walk active columns in band order. `add_usd` adds to running;
 * `multiply` and `margin_denom` set running from source (markup / priced amount).
 */
export function evaluatePricingLayers(input: PricingLayerEvaluateInput): PricingLayerEvaluateResult {
  const materials = Number.isFinite(input.materials_cost_usd) ? input.materials_cost_usd : 0;
  const warnings: string[] = [];
  const ordered = sortPricingLayerColumns(input.columns);
  const columnResultsMap: Record<string, number> = {};
  const column_results: PricingLayerColumnResult[] = [];
  const bandTotals: Record<PricingLayerBand, number> = {
    cogs: materials,
    wholesale: materials,
    msrp: materials,
  };

  let running = materials;
  let currentBand: PricingLayerBand | null = null;

  for (const col of ordered) {
    if (currentBand === null && col.band === "msrp") {
      bandTotals.wholesale = roundedBandTotal("wholesale", bandTotals.cogs, input.round_up);
      running = bandTotals.wholesale;
    }
    if (currentBand !== null && col.band !== currentBand) {
      bandTotals[currentBand] = running;
      const from = bandRank(currentBand);
      const to = bandRank(col.band);
      for (let i = from + 1; i < to; i++) {
        bandTotals[BAND_ORDER[i]] = running;
      }
      if (col.band === "wholesale") {
        running = bandTotals.cogs;
      } else if (col.band === "msrp") {
        if (from < bandRank("wholesale")) {
          bandTotals.wholesale = bandTotals.cogs;
        }
        bandTotals.wholesale = roundedBandTotal("wholesale", bandTotals.wholesale, input.round_up);
        running = bandTotals.wholesale;
      }
    }
    currentBand = col.band;

    const raw = input.values_by_column_id[col.id];
    const input_value =
      raw === null || raw === undefined || !Number.isFinite(Number(raw)) ? null : Number(raw);

    const source_amount = resolveSourceAmount({
      col,
      materials,
      running,
      bandTotals,
      columnResults: columnResultsMap,
      warnings,
    });

    let result_amount: number | null = null;

    if (col.op === "add_usd") {
      if (input_value === null) {
        result_amount = 0;
      } else {
        result_amount = round4(input_value);
        running = round4(running + result_amount);
      }
    } else if (col.op === "multiply") {
      if (input_value === null || source_amount === null) {
        warnings.push(`Column ${col.key}: multiply needs source and factor.`);
        result_amount = null;
      } else if (col.rate_kind === "markup") {
        if (input_value < 0) {
          warnings.push(`Column ${col.key}: markup fraction must be zero or greater.`);
          result_amount = null;
        } else {
          result_amount = round4(source_amount * (1 + input_value));
          running = result_amount;
        }
      } else {
        result_amount = round4(source_amount * input_value);
        running = result_amount;
      }
    } else if (col.op === "margin_denom") {
      if (input_value === null || source_amount === null) {
        warnings.push(`Column ${col.key}: margin_denom needs source and margin fraction.`);
        result_amount = null;
      } else if (input_value <= 0 || input_value >= 1) {
        warnings.push(`Column ${col.key}: margin fraction must be in (0, 1).`);
        result_amount = null;
      } else {
        result_amount = round4(source_amount / (1 - input_value));
        running = result_amount;
      }
    }

    if (result_amount !== null) {
      columnResultsMap[col.id] = result_amount;
    }

    column_results.push({
      column_id: col.id,
      key: col.key,
      band: col.band,
      op: col.op,
      input_value,
      source_amount,
      result_amount,
      running_after: running,
    });
  }

  if (currentBand) {
    bandTotals[currentBand] = roundedBandTotal(currentBand, running, input.round_up);
  }

  // Ensure later band totals reflect cascade when a band had no columns.
  if (!ordered.some((c) => c.band === "cogs")) {
    bandTotals.cogs = materials;
  }
  if (!ordered.some((c) => c.band === "wholesale")) {
    bandTotals.wholesale = roundedBandTotal("wholesale", bandTotals.cogs, input.round_up);
  }
  if (!ordered.some((c) => c.band === "msrp")) {
    bandTotals.msrp = roundedBandTotal("msrp", bandTotals.wholesale, input.round_up);
  } else if (!ordered.some((c) => c.band === "wholesale") && ordered.some((c) => c.band === "msrp")) {
    // msrp loop already started from the rounded wholesale (= rounded cogs when wholesale is empty)
  }

  return {
    materials_cost_usd: round4(materials),
    column_results,
    cogs_total_usd: round4(bandTotals.cogs),
    wholesale_total_usd: round4(bandTotals.wholesale),
    msrp_total_usd: round4(bandTotals.msrp),
    warnings,
  };
}

/** Convert legacy margin denom (e.g. 0.88) to margin fraction (0.12). */
export function marginFractionFromDenom(denom: number | null | undefined): number | null {
  if (denom === null || denom === undefined || !Number.isFinite(denom)) return null;
  if (denom <= 0 || denom >= 1) return null;
  return round4(1 - denom);
}
